<template>
  <div class="app-manage">
    <div class="content-box">
      <div class="toolbar">
        <div class="toolbar-right">
          <el-button type="primary" size="small" @click="showAddDialog" class="add-btn">
            <i class="el-icon-plus"></i>
            {{$t('openPlatform.addApp')}}
          </el-button>
        </div>
      </div>

      <el-table
        :data="filteredAppList"
        style="width: 100%"
        v-loading="loading"
        height="calc(100vh - 280px)"
      >
        <el-table-column prop="app_name" :label="$t('openPlatform.appName')" width="150" header-align="center" />
        <el-table-column prop="description" :label="$t('openPlatform.appDescription')" header-align="center" />
        <el-table-column prop="app_id" :label="$t('openPlatform.appId')" width="220" header-align="center">
          <template slot-scope="scope">
            <div style="display: flex; align-items: center; gap: 8px;">
              <span>{{ scope.row.app_id }}</span>
              <el-button 
                type="text" 
                size="mini"
                @click="copyToClipboard(scope.row.app_id, $t('openPlatform.appId'))"
                style="color: #366aff; padding: 0; min-width: auto;"
              >
                <i class="el-icon-document-copy"></i>
              </el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('openPlatform.appSecret')" width="200" header-align="center">
          <template slot-scope="scope">
            <el-button 
              type="text" 
              @click="showAppSecret(scope.row)"
              style="color: #366aff;"
            >
              {{$t('openPlatform.viewSecret')}}
            </el-button>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" :label="$t('openPlatform.createTime')" width="180" header-align="center">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('openPlatform.operation')" width="200" fixed="right" header-align="center">
          <template slot-scope="scope">
            <el-button size="mini" @click="editApp(scope.row)">{{$t('openPlatform.edit')}}</el-button>
            <el-button size="mini" type="danger" @click="deleteApp(scope.row)" class="delete-btn">{{$t('openPlatform.delete')}}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 新增/编辑应用对话框 -->
    <popup v-if="dialogVisible" @closeEvent="closeDialog">
      <template slot="popup-name">
        <div>{{ dialogTitle }}</div>
      </template>
      <template slot="popup-tip">
        {{$t('openPlatform.addAppTitle')}}
      </template>
      <template slot="popup-con">
        <div class="popup-con">
          <div class="field-item-class-c">
            <div class="field-name">
              {{$t('openPlatform.appName')}}
              <span class="required-mark">*</span>
            </div>
            <el-input 
              v-model="appForm.app_name" 
              :placeholder="$t('openPlatform.appNameRequired')"
            />
          </div>
          <div class="field-item-class-c">
            <div class="field-name">{{$t('openPlatform.appDescription')}}</div>
            <el-input
              v-model="appForm.description"
              type="textarea"
              :rows="4"
              :placeholder="$t('openPlatform.appDescriptionRequired')"
            />
          </div>
        </div>
      </template>
      <template slot="dialog-footer">
        <el-button class="cancel-btn" @click="closeDialog">{{$t('openPlatform.cancel')}}</el-button>
        <el-button class="confirm-btn" @click="submitForm" :loading="submitLoading">{{$t('openPlatform.confirm')}}</el-button>
      </template>
    </popup>
  </div>
</template>

<script>
import popup from "@/components/popup.vue";

export default {
  name: 'AppManage',
  components: {
    popup
  },
  data() {
    return {
      loading: false,
      submitLoading: false,
      appList: [],
      dialogVisible: false,
      dialogTitle: this.$t('openPlatform.addAppTitle'),
      appForm: {
        app_name: '',
        description: ''
      }
    }
  },
  computed: {
    filteredAppList() {
      return this.appList;
    }
  },
  mounted() {
    this.loadAppList();
  },
  methods: {
    // 加载应用列表
    async loadAppList() {
      try {
        this.loading = true;
        const mainId = localStorage.getItem('_mainId');
        const response = await this.$http.get(`/openplatform-api/applications?main_id=${mainId}`);
        
        if (response.data && response.data.applications) {
          this.appList = response.data.applications || [];
        } else if (response.data && Array.isArray(response.data)) {
          // 如果直接返回数组
          this.appList = response.data || [];
        }
        // 不显示错误信息，静默处理
      } catch (error) {
        console.error('获取应用列表失败:', error);
        // 不显示错误信息，静默处理
      } finally {
        this.loading = false;
      }
    },

    // 显示新增对话框
    showAddDialog() {
      this.dialogTitle = this.$t('openPlatform.addAppTitle');
      this.dialogVisible = true;
      this.resetForm();
    },

    // 编辑应用
    async editApp(row) {
      try {
        this.loading = true;
        const response = await this.$http.get(`/openplatform-api/applications/${row.app_id}`);
        
        if (response.data && response.data.app_id) {
          // 如果返回的是应用对象
          this.appForm = { 
            app_name: response.data.app_name,
            description: response.data.description,
            app_id: response.data.app_id // 保存app_id用于更新
          };
          this.dialogTitle = this.$t('openPlatform.editAppTitle');
          this.dialogVisible = true;
        } else if (response.data && response.data.code === '0') {
          // 如果返回的是包装格式
          this.appForm = { 
            app_name: response.data.data.app_name,
            description: response.data.data.description,
            app_id: response.data.data.app_id
          };
          this.dialogTitle = this.$t('openPlatform.editAppTitle');
          this.dialogVisible = true;
        } else {
          // 如果获取详情失败，直接使用行数据
          this.appForm = { 
            app_name: row.app_name,
            description: row.description,
            app_id: row.app_id
          };
          this.dialogTitle = this.$t('openPlatform.editAppTitle');
          this.dialogVisible = true;
        }
      } catch (error) {
        console.error('获取应用详情失败:', error);
        // 如果获取详情失败，直接使用行数据
        this.appForm = { 
          app_name: row.app_name,
          description: row.description,
          app_id: row.app_id
        };
        this.dialogTitle = this.$t('openPlatform.editAppTitle');
        this.dialogVisible = true;
      } finally {
        this.loading = false;
      }
    },

    // 删除应用
    deleteApp(row) {
      this.$confirm(this.$t('openPlatform.deleteConfirm'), this.$t('openPlatform.title'), {
        confirmButtonText: this.$t('openPlatform.confirm'),
        cancelButtonText: this.$t('openPlatform.cancel'),
        type: 'warning'
      }).then(async () => {
        try {
          this.loading = true;
          const response = await this.$http.delete(`/openplatform-api/applications/${row.app_id}`);
          
          // 根据实际API响应格式处理
          if (response.data && response.data.code === '0') {
            this.$message.success(this.$t('openPlatform.deleteSuccess'));
            this.loadAppList(); // 重新加载列表
          } else if (response.status === 200 || response.status === 204) {
            // 删除成功，通常返回200或204状态码
            this.$message.success(this.$t('openPlatform.deleteSuccess'));
            this.loadAppList(); // 重新加载列表
          } else {
            this.$message.error((response.data && response.data.message) || this.$t('openPlatform.deleteFail'));
          }
        } catch (error) {
          console.error('删除应用失败:', error);
          this.$message.error(this.$t('openPlatform.deleteFail'));
        } finally {
          this.loading = false;
        }
      });
    },

    // 提交表单
    async submitForm() {
      // 简单验证
      if (!this.appForm.app_name.trim()) {
        this.$message.error(this.$t('openPlatform.appNameRequired'));
        return;
      }

      try {
        this.submitLoading = true;
        
        // 从localStorage获取必要的ID
        const mainId = localStorage.getItem('_mainId');
        const uid = localStorage.getItem('_uid');
        
        if (!mainId || !uid) {
          this.$message.error(this.$t('openPlatform.userInfoMissing'));
          return;
        }

        const requestData = {
          app_name: this.appForm.app_name.trim(),
          description: this.appForm.description.trim(),
          main_id: mainId,
          creator_id: uid,
          callback_url: 'https://example.com/callback', // 默认回调地址
          contact_email: 'contact@example.com', // 默认联系邮箱
          contact_phone: '13800138000' // 默认联系电话
        };

        let response;
        if (this.dialogTitle === this.$t('openPlatform.addAppTitle')) {
          // 新增应用 - 使用正确的接口路径
          response = await this.$http.post(`/openplatform-api/applications/register`, requestData);
        } else {
          // 编辑应用 - 使用PUT接口
          response = await this.$http.put(`/openplatform-api/applications/${this.appForm.app_id}`, requestData);
        }
        
        // 根据实际API响应格式处理
        if (response.data && response.data.app_id) {
          // 创建/更新成功，返回的是应用对象
          this.$message.success(this.dialogTitle === this.$t('openPlatform.addAppTitle') ? this.$t('openPlatform.addSuccess') : this.$t('openPlatform.editSuccess'));
          this.dialogVisible = false;
          this.loadAppList(); // 重新加载列表
        } else if (response.data && response.data.code === '0') {
          // 如果后端返回的是包装格式
          this.$message.success(this.dialogTitle === this.$t('openPlatform.addAppTitle') ? this.$t('openPlatform.addSuccess') : this.$t('openPlatform.editSuccess'));
          this.dialogVisible = false;
          this.loadAppList(); // 重新加载列表
        } else {
          this.$message.error((response.data && response.data.message) || this.$t('openPlatform.operationFail'));
        }
      } catch (error) {
        console.error('操作失败:', error);
        this.$message.error(this.$t('openPlatform.operationFail'));
      } finally {
        this.submitLoading = false;
      }
    },

    // 关闭对话框
    closeDialog() {
      this.dialogVisible = false;
      this.resetForm();
    },

    // 重置表单
    resetForm() {
      this.appForm = {
        app_name: '',
        description: '',
        app_id: '' // 重置app_id
      };
    },

    // 格式化日期时间
    formatDateTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },

    // 显示应用密钥
    showAppSecret(app) {
      this.$alert(
        `<div style="text-align: left; line-height: 1.8; padding: 20px;">
          <div style="margin-bottom: 20px; color: #606266; font-size: 16px; font-weight: bold; text-align: center;">${this.$t('openPlatform.appSecretInfo')}</div>
          
          <div style="margin-bottom: 20px;">
            <div style="color: #606266; font-size: 14px; margin-bottom: 8px;">${this.$t('openPlatform.appId')}</div>
            <div style="display: flex; align-items: center; gap: 10px;">
              <span style="font-family: monospace; background: #f5f7fa; padding: 8px 12px; border-radius: 4px; color: #303133; font-size: 14px; flex: 1; word-break: break-all;">${app.app_id}</span>
              <button class="copy-btn" onclick="copyToClipboard('${app.app_id}', '${this.$t('openPlatform.appId')}')" style="background: #366aff; color: white; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 12px;">${this.$t('openPlatform.copy')}</button>
            </div>
          </div>
          
          <div style="margin-bottom: 20px;">
            <div style="color: #606266; font-size: 14px; margin-bottom: 8px;">${this.$t('openPlatform.appSecret')}</div>
            <div style="display: flex; align-items: center; gap: 10px;">
              <span style="font-family: monospace; background: #f5f7fa; padding: 8px 12px; border-radius: 4px; color: #e6a23c; font-size: 14px; flex: 1; word-break: break-all;">${app.app_secret}</span>
              <button class="copy-btn" onclick="copyToClipboard('${app.app_secret}', '${this.$t('openPlatform.appSecret')}')" style="background: #366aff; color: white; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 12px;">${this.$t('openPlatform.copy')}</button>
            </div>
          </div>
          
          <div style="margin-top: 20px; color: #909399; font-size: 12px; text-align: center;">
            <i class="el-icon-warning"></i> ${this.$t('openPlatform.secretWarning')}
          </div>
        </div>`, 
        this.$t('openPlatform.appSecretInfo'), 
        {
          confirmButtonText: this.$t('openPlatform.confirm'),
          customClass: 'app-secret-alert',
          dangerouslyUseHTMLString: true,
          showClose: false,
          closeOnClickModal: false,
          closeOnPressEscape: false,
          showCancelButton: false,
          type: 'success'
        }
      );
      
      // 添加复制功能到全局
      window.copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success(this.$t('openPlatform.copySuccess'));
        }).catch(() => {
          // 降级方案
          const textArea = document.createElement('textarea');
          textArea.value = text;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          this.$message.success(this.$t('openPlatform.copySuccess'));
        });
      };
    },

    // 复制到剪贴板
    copyToClipboard(text, label) {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success(this.$t('openPlatform.copySuccess'));
      }).catch(() => {
        // 降级方案
        const textArea = document.createElement('textArea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.$message.success(this.$t('openPlatform.copySuccess'));
      });
    }
  }
}
</script>

<style scoped lang="less">
.app-manage {
  height: 100%;
  .content-box {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0px;
  }
  
  .page-header {
    margin-bottom: 24px;
    
    h2 {
      font-size: 20px;
      color: #303133;
      margin: 0 0 8px 0;
    }
    
    p {
      font-size: 14px;
      color: #909399;
      margin: 0;
    }
  }
  
  .toolbar {
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: flex-end; /* 改为右对齐 */
  }

  .toolbar-right {
    display: flex;
    align-items: center;
  }

  .add-btn {
    color: #fff !important; /* Changed text color to white */
    border-radius: 20px !important; 
  }

  .delete-btn {
    color: #fff !important; /* 删除按钮文字颜色为白色 */
  }
  
  .el-table {
    flex: 1;
    
    /deep/ .el-table__header {
      background-color: #f5f7fa !important;
      border-top: 2px solid #e8eaed !important;
      border-radius: 4px 4px 0 0 !important;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    }
    
    /deep/ .el-table__body {
      background-color: white !important;
    }
    
    /deep/ .cell {
      color: #333 !important;
    }
  }
  
  /* 空数据样式 */
  .empty-data {
    text-align: center;
    padding: 60px 20px;
    color: #909399;
    
    i {
      font-size: 48px;
      margin-bottom: 16px;
      display: block;
    }
    
    p {
      font-size: 16px;
      margin-bottom: 24px;
    }
  }

  
  /deep/ .el-input__inner {
    color: #333 !important;
    background-color: white !important;
  }
}

/* 弹窗样式继承自popup.vue */
.popup-con {
  .field-item-class-c {
    .field-name {
      padding-bottom: 8px;
      line-height: 20px;
      color: #616161;
      
      .required-mark {
        color: #f56c6c !important;
        margin-left: 4px;
        font-weight: bold;
      }
    }
    
    .el-input {
      width: 100%;
    }
    
    .el-textarea {
      width: 100%;
    }
    
    .el-button {
      color: #ffffff;
    }
    
    .confirm-btn {
      color: #ffffff !important;
    }
  }
}

/* 应用密钥弹窗样式 */
/deep/ .app-secret-alert {
  .el-message-box__header {
    background-color: #f5f7fa;
    border-bottom: 1px solid #ebeef5;
    padding: 15px 20px;
    border-radius: 4px 4px 0 0;
  }

  .el-message-box__title {
    color: #303133;
    font-size: 16px;
    font-weight: bold;
  }

  .el-message-box__content {
    padding: 20px;
    color: #606266;
    font-size: 14px;
    line-height: 1.6;
    
    /* 确保HTML内容正确显示 */
    .monospace {
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    }
  }

  .el-message-box__btns {
    padding: 15px 20px;
    border-top: 1px solid #ebeef5;
    border-radius: 0 0 4px 4px;
  }

  .el-message-box__btns .el-button--primary {
    background-color: #366aff;
    border-color: #366aff;
    border-radius: 4px;
    padding: 8px 20px;
  }
  
  /* 确保弹窗有合适的宽度和高度 */
  .el-message-box {
    min-width: 600px;
    min-height: 400px;
  }
}

/* 确保弹窗中确定按钮的文字颜色是白色 */
/deep/ .popup-floating-layer {
  .confirm-btn {
    color: #ffffff !important;
  }
  
  .el-button--primary {
    color: #ffffff !important;
  }
}
</style> 